@if (isLoading()) {
  <div class="flex justify-center items-center h-64">
    <mat-spinner diameter="48" />
  </div>
} @else {
  <div class="p-5">
    <app-breadcrumb [items]="breadcrumbItems()" />
    <!-- Header -->
    <div class="flex justify-between items-center mb-5">
      <h1 class="text-2xl font-medium">
        @if (isEditMode() && waterTitle()) {
          {{ waterTitle() }}
        } @else {
          {{ 'waters.form.create-title' | translate }}
        }
      </h1>
      <div class="flex gap-2">
        @switch (selectedTabIndex()) {
          @case (tabIndex.fishingSpots) {
            <button mat-flat-button color="primary" (click)="router.navigate(['/waters/edit', waterId(), 'spots', 'create'])">
              <mat-icon>add</mat-icon>
              {{ 'waters.fishing-spots.add' | translate }}
            </button>
          }
          @case (tabIndex.accommodations) {
            <button mat-flat-button color="primary" (click)="router.navigate(['/waters/edit', waterId(), 'accommodations', 'create'])">
              <mat-icon>add</mat-icon>
              {{ 'waters.accommodations.add' | translate }}
            </button>
          }
          @case (tabIndex.information) {
            <button mat-flat-button color="primary" (click)="router.navigate(['/waters/edit', waterId(), 'information'])">
              <mat-icon>edit</mat-icon>
              {{ 'waters.information.edit-button' | translate }}
            </button>
          }
          <!--
            The Pricing tab falls through to the same Delete / Cancel / Save as the form tabs: one Save
            persists the lake and then folds in the exclusive pricing grid (see WaterDetailsComponent.save),
            so there is no per-tab Save to reason about.
          -->
          @default {
            @if (isEditMode()) {
              <button
                mat-stroked-button
                color="warn"
                class="!min-w-0 !px-2"
                [disabled]="isDeleting() || isSaving()"
                [matTooltip]="'waters.form.delete' | translate"
                (click)="delete()">
                @if (isDeleting()) {
                  <mat-spinner diameter="18" />
                } @else {
                  <span class="flex items-center leading-none"><mat-icon>delete</mat-icon></span>
                }
              </button>
            }
            <button mat-stroked-button [disabled]="isSaving() || isDeleting()" (click)="cancel()">
              {{ 'waters.form.cancel' | translate }}
            </button>
            <button
              mat-flat-button
              color="primary"
              [disabled]="isSaving() || isDeleting() || isPricingLoading()"
              (click)="save()">
              @if (isSaving()) {
                <mat-spinner diameter="18" />
              } @else {
                {{ 'waters.form.save' | translate }}
              }
            </button>
          }
        }
      </div>
    </div>

    <mat-tab-group animationDuration="0ms" [selectedIndex]="selectedTabIndex()" (selectedIndexChange)="selectedTabIndex.set($event)">
      <!-- General tab (CS-208) -->
      <mat-tab [label]="'waters.form.tab.general' | translate">
        <form [formGroup]="form" class="pt-4 flex flex-col gap-4">

          <!-- Identity -->
          <mat-card>
            <mat-card-header>
              <mat-card-title>{{ 'waters.form.section.identity' | translate }}</mat-card-title>
              <mat-card-subtitle>{{ 'waters.form.section.identity-sub' | translate }}</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content class="!pt-4 flex flex-col gap-4">
              <!-- Row 1: Name | URL slug -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <mat-form-field>
                  <mat-label>{{ 'waters.form.title' | translate }}</mat-label>
                  <input matInput formControlName="title" />
                  @if (form.controls.title.hasError('required') && form.controls.title.touched) {
                    <mat-error>{{ 'waters.form.title-required' | translate }}</mat-error>
                  }
                </mat-form-field>

                <mat-form-field>
                  <mat-label>{{ 'waters.form.urlTitle' | translate }}</mat-label>
                  <input matInput formControlName="urlTitle" />
                  <mat-hint>{{ 'waters.form.urlTitle-hint' | translate }}</mat-hint>
                </mat-form-field>
              </div>

              <!-- Row 2: Country | Shown on sites -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <mat-form-field>
                  <mat-label>{{ 'waters.form.country' | translate }}</mat-label>
                  <mat-select formControlName="fkCountryId">
                    @for (c of codeListsService.countries(); track c.id) {
                      <mat-option [value]="c.id">{{ c.name }}</mat-option>
                    }
                  </mat-select>
                  @if (form.controls.fkCountryId.hasError('required') && form.controls.fkCountryId.touched) {
                    <mat-error>{{ 'waters.form.country-required' | translate }}</mat-error>
                  }
                </mat-form-field>

                @if (codeListsService.sites().length > 0) {
                  <div>
                    <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{{ 'waters.form.sites' | translate }}</p>
                    <mat-chip-listbox formControlName="siteIds" multiple>
                      @for (s of codeListsService.sites(); track s.id) {
                        <mat-chip-option [value]="s.id">{{ s.name }}</mat-chip-option>
                      }
                    </mat-chip-listbox>
                  </div>
                }
              </div>

              <!-- Row 3: Active toggle -->
              <mat-slide-toggle formControlName="isActive">
                {{ 'waters.form.isActive' | translate }}
              </mat-slide-toggle>
            </mat-card-content>
          </mat-card>

          <!-- Specifications -->
          <mat-card>
            <mat-card-header>
              <mat-card-title>{{ 'waters.form.section.specifications' | translate }}</mat-card-title>
              <mat-card-subtitle>{{ 'waters.form.section.specifications-sub' | translate }}</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content class="!pt-4 flex flex-col gap-4">
              <!-- Row 1: Size | Record carp -->
              <div class="grid grid-cols-2 gap-4">
                <mat-form-field>
                  <mat-label>{{ 'waters.form.waterSize' | translate }}</mat-label>
                  <input matInput type="number" formControlName="waterSize" min="0" />
                  <span matSuffix class="px-2">ha</span>
                  @if (form.controls.waterSize.hasError('required') && form.controls.waterSize.touched) {
                    <mat-error>{{ 'waters.form.waterSize-required' | translate }}</mat-error>
                  }
                </mat-form-field>

                <mat-form-field>
                  <mat-label>{{ 'waters.form.carpWeight' | translate }}</mat-label>
                  <input matInput type="number" formControlName="carpWeight" min="0" />
                  <span matSuffix class="px-2">kg</span>
                  @if (form.controls.carpWeight.hasError('required') && form.controls.carpWeight.touched) {
                    <mat-error>{{ 'waters.form.carpWeight-required' | translate }}</mat-error>
                  }
                </mat-form-field>
              </div>

              <!-- Row 2: Difficulty slider — left column only -->
              <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col gap-1">
                  <label class="text-sm text-gray-600 dark:text-gray-400">
                    {{ 'waters.form.difficulty' | translate }}: {{ difficultyDisplay() }}
                  </label>
                  <mat-slider min="1" max="5" step="1" discrete class="w-full">
                    <input matSliderThumb formControlName="difficulty" />
                  </mat-slider>
                </div>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Booking rules -->
          <mat-card>
            <mat-card-header>
              <mat-card-title>{{ 'waters.form.section.booking-rules' | translate }}</mat-card-title>
              <mat-card-subtitle>{{ 'waters.form.section.booking-rules-sub' | translate }}</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content class="!pt-4 flex flex-col gap-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <mat-form-field>
                  <mat-label>{{ 'waters.form.spots' | translate }}</mat-label>
                  <input matInput type="number" formControlName="spots" min="0" />
                  @if (form.controls.spots.hasError('required') && form.controls.spots.touched) {
                    <mat-error>{{ 'waters.form.spots-required' | translate }}</mat-error>
                  }
                </mat-form-field>

                <mat-form-field>
                  <mat-label>{{ 'waters.form.coTravelerSpots' | translate }}</mat-label>
                  <input matInput type="number" formControlName="coTravelerSpots" min="0" />
                  @if (form.controls.coTravelerSpots.hasError('required') && form.controls.coTravelerSpots.touched) {
                    <mat-error>{{ 'waters.form.coTravelerSpots-required' | translate }}</mat-error>
                  }
                </mat-form-field>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'waters.form.exclusive' | translate }}</label>
                  <mat-radio-group formControlName="fkExclusiveId" class="flex flex-row flex-wrap gap-4">
                    @for (e of codeListsService.exclusives(); track e.id) {
                      <mat-radio-button [value]="e.id">{{ e.name }}</mat-radio-button>
                    }
                  </mat-radio-group>
                  @if (form.controls.fkExclusiveId.hasError('required') && form.controls.fkExclusiveId.touched) {
                    <span class="text-red-600 text-sm">{{ 'waters.form.exclusive-required' | translate }}</span>
                  }
                </div>

                <mat-slide-toggle formControlName="dogs">
                  {{ 'waters.form.dogs' | translate }}
                </mat-slide-toggle>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Booking & price -->
          <mat-card>
            <mat-card-header>
              <mat-card-title>{{ 'waters.form.section.pricing' | translate }}</mat-card-title>
              <mat-card-subtitle>{{ 'waters.form.section.pricing-sub' | translate }}</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content class="!pt-4 flex flex-col gap-5">
              <div class="mb-2">
                <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ 'waters.form.bookable-for' | translate }}</p>
                <p class="text-xs text-gray-500 mb-3">{{ 'waters.form.bookable-for-hint' | translate }}</p>
                <mat-chip-listbox
                  multiple
                  [value]="selectedBookable()"
                  (change)="onBookableChange($event)">
                  <mat-chip-option value="week">{{ 'waters.form.week' | translate }}</mat-chip-option>
                  <mat-chip-option value="midweek">{{ 'waters.form.midweek' | translate }}</mat-chip-option>
                  <mat-chip-option value="weekend">{{ 'waters.form.weekend' | translate }}</mat-chip-option>
                </mat-chip-listbox>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                <mat-form-field>
                  <mat-label>{{ 'waters.form.price' | translate }}</mat-label>
                  <span matPrefix class="px-2">€</span>
                  <input matInput type="number" formControlName="price" min="0" />
                  <mat-hint>{{ 'waters.form.price-hint' | translate }}</mat-hint>
                  @if (form.controls.price.hasError('required') && form.controls.price.touched) {
                    <mat-error>{{ 'waters.form.price-required' | translate }}</mat-error>
                  }
                </mat-form-field>

                <mat-slide-toggle formControlName="pricePerSpot">
                  {{ 'waters.form.pricePerSpot' | translate }}
                </mat-slide-toggle>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Lake features -->
          <mat-card>
            <mat-card-header>
              <mat-card-title>{{ 'waters.form.section.facilities' | translate }}</mat-card-title>
              <mat-card-subtitle>{{ 'waters.form.section.facilities-sub' | translate }}</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content class="!pt-4 flex flex-col gap-6">

              <!-- Amenities (from codelist) -->
              @if (codeListsService.amenities().length > 0) {
                <div>
                  <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ 'waters.form.amenities' | translate }}</p>
                  <p class="text-xs text-gray-500 mb-3">{{ 'waters.form.amenities-hint' | translate }}</p>
                  <mat-chip-listbox formControlName="amenityIds" multiple>
                    @for (a of codeListsService.amenities(); track a.id) {
                      <mat-chip-option [value]="a.id">{{ a.name }}</mat-chip-option>
                    }
                  </mat-chip-listbox>
                </div>
              }

              <!-- Feature toggles -->
              <div>
                <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{{ 'waters.form.features' | translate }}</p>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <mat-slide-toggle formControlName="carOnSpot">{{ 'waters.form.carOnSpot' | translate }}</mat-slide-toggle>
                  <mat-slide-toggle formControlName="baitBoat">{{ 'waters.form.baitBoat' | translate }}</mat-slide-toggle>
                  <mat-slide-toggle formControlName="rowingBoat">{{ 'waters.form.rowingBoat' | translate }}</mat-slide-toggle>
                  <mat-slide-toggle formControlName="restaurant">{{ 'waters.form.restaurant' | translate }}</mat-slide-toggle>
                  <mat-slide-toggle formControlName="hasAccommodations">{{ 'waters.form.hasAccommodations' | translate }}</mat-slide-toggle>
                  <mat-slide-toggle formControlName="ownTent">{{ 'waters.form.ownTent' | translate }}</mat-slide-toggle>
                  <mat-slide-toggle formControlName="ownCaravanCamper">{{ 'waters.form.ownCaravanCamper' | translate }}</mat-slide-toggle>
                </div>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Contact -->
          <mat-card>
            <mat-card-header>
              <mat-card-title>{{ 'waters.form.section.contact' | translate }}</mat-card-title>
              <mat-card-subtitle>{{ 'waters.form.section.contact-sub' | translate }}</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content class="!pt-4">
              @if (missingServiceOrderEmail()) {
                <div class="mb-4 border-l-4 border-amber-500 bg-amber-50 rounded p-3 flex items-start gap-3">
                  <mat-icon class="!text-amber-500">warning</mat-icon>
                  <p class="m-0 text-sm">{{ 'waters.form.missing-service-email-warning' | translate }}</p>
                </div>
              }
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <!-- Water services email -->
                <mat-form-field>
                  <mat-label>{{ 'waters.form.waterServicesEmail' | translate }}</mat-label>
                  <input matInput type="email" formControlName="waterServicesEmail" />
                  <mat-hint>{{ 'waters.form.waterServicesEmail-hint' | translate }}</mat-hint>
                  @if (form.controls.waterServicesEmail.hasError('email') && form.controls.waterServicesEmail.touched) {
                    <mat-error>{{ 'waters.form.waterServicesEmail-invalid' | translate }}</mat-error>
                  }
                </mat-form-field>

                <!-- Mailplus -->
                <mat-form-field>
                  <mat-label>{{ 'waters.form.mailplusList' | translate }}</mat-label>
                  <input matInput type="number" formControlName="mailplusList" />
                  <mat-hint>{{ 'waters.form.mailplusList-hint' | translate }}</mat-hint>
                </mat-form-field>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Water owners -->
          <mat-card>
            <mat-card-header>
              <mat-card-title>{{ 'waters.form.section.water-owners' | translate }}</mat-card-title>
              <mat-card-subtitle>{{ 'waters.form.section.water-owners-sub' | translate }}</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content class="!pt-4">
              @if (waterId(); as id) {
                <app-water-owner-list [waterId]="id" (ownersChanged)="owners.set($event)" />
              } @else {
                <p class="text-gray-500 text-sm">{{ 'waters.owners.save-first' | translate }}</p>
              }
            </mat-card-content>
          </mat-card>

          <!-- Voucher text -->
          <mat-card>
            <mat-card-header>
              <mat-card-title>{{ 'waters.form.section.voucher' | translate }}</mat-card-title>
              <mat-card-subtitle>{{ 'waters.form.section.voucher-sub' | translate }}</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content class="!pt-4">
              <app-localized-text-editor
                [nl]="form.controls.voucherTextNl"
                [en]="form.controls.voucherTextEn"
                [de]="form.controls.voucherTextDe"
                [it]="form.controls.voucherTextIt"
                [fr]="form.controls.voucherTextFr"
              />
            </mat-card-content>
          </mat-card>

        </form>
      </mat-tab>

      <!-- Location tab (CS-208) -->
      <mat-tab [label]="'waters.form.tab.location' | translate">
        <form [formGroup]="form" class="pt-4 flex flex-col gap-4">
          <mat-card>
            <mat-card-header>
              <mat-card-title>{{ 'waters.form.section.location' | translate }}</mat-card-title>
              <mat-card-subtitle>{{ 'waters.form.section.location-sub' | translate }}</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content class="!pt-4 flex flex-col gap-4">
              <app-location-picker
                [latControl]="form.controls.latitude"
                [lngControl]="form.controls.longitude"
              />
            </mat-card-content>
          </mat-card>
        </form>
      </mat-tab>
      <mat-tab [label]="'waters.form.tab.fishing-spots' | translate" [disabled]="!isEditMode()">
        @if (waterId() !== null) {
          <app-fishing-spot-list [waterId]="waterId()!" class="block pt-4" />
        }
      </mat-tab>
      <mat-tab [label]="'waters.form.tab.accommodations' | translate" [disabled]="!isEditMode()">
        @if (waterId() !== null) {
          <app-accommodation-list [waterId]="waterId()!" class="block pt-4" />
        }
      </mat-tab>
      <mat-tab [label]="'waters.form.tab.information' | translate" [disabled]="!isEditMode()">
        @if (waterId() !== null) {
          <app-water-information-card [waterId]="waterId()!" class="block pt-4" />
        }
      </mat-tab>
      <mat-tab [label]="'waters.form.tab.pricing' | translate" [disabled]="!isEditMode()">
        @if (waterId() !== null) {
          <div class="pt-4 flex flex-col gap-4">
            <!--
              The lake's price lists (CS-232) — the dated versions everything below is priced within, so
              they come first. Each row is written the moment it is confirmed, deliberately outside the
              page's staged Save; see WaterPriceListsComponent. Held back while that Save is in flight so
              a price list cannot be deleted from under the exclusive grid's pending PUT.
            -->
            <app-water-price-lists
              class="block"
              [waterId]="waterId()!"
              [disabled]="isSaving()"
              (changed)="onPriceListsChanged()" />

            <!-- Pricing details -->
            <mat-card>
              <mat-card-header>
                <mat-card-title>{{ 'waters.pricing.card-title' | translate }}</mat-card-title>
                <mat-card-subtitle>{{ 'waters.pricing.card-hint' | translate }}</mat-card-subtitle>
              </mat-card-header>
              <mat-card-content class="!pt-4">
                <button mat-flat-button color="primary" (click)="editPricing()">
                  <mat-icon>edit</mat-icon>
                  {{ 'waters.pricing.edit' | translate }}
                </button>
              </mat-card-content>
            </mat-card>

            <!-- Flat per-market fees (CS-287). Applies to every lake, so it sits ahead of the exclusive
                 grid, which only some lakes have. Renders its own card, as the exclusive section does. -->
            <app-water-market-fees class="block" [waterId]="waterId()!" />

            <!--
              Whole-lake exclusive pricing (CS-286). The gate and the grid's axes come from the *saved*
              water, so while the water is still loading nothing is rendered — showing the
              "not available for exclusive hire" hint against an unloaded water would be wrong.
            -->
            @if (!isLoading()) {
              <app-exclusive-pricing
                class="block"
                [waterId]="waterId()!"
                [canEdit]="canEditExclusivePrices()"
                [hasUnsavedExclusiveChange]="hasUnsavedExclusiveChange()"
                [isSaving]="isSaving()" />
            }
          </div>
        }
      </mat-tab>

      <mat-tab [disabled]="!isEditMode()">
        <ng-template mat-tab-label>
          {{ 'waters.form.tab.reviews' | translate }}
          <app-count-badge [count]="reviewsCount()" />
        </ng-template>
        @if (isEditMode()) {
          <app-water-reviews [waterId]="waterId()!" (totalCount)="reviewsCount.set($event)" />
        }
      </mat-tab>

      <!-- Media tab (CS-214) — user-submitted media batches, edit mode only -->
      <mat-tab [disabled]="!isEditMode()">
        <ng-template mat-tab-label>
          {{ 'waters.form.tab.media' | translate }}
          <app-count-badge [count]="mediaBatchCount()" />
        </ng-template>
        @if (isEditMode() && waterId() !== null) {
          <app-water-user-media-batch-list [waterId]="waterId()!" (totalCount)="mediaBatchCount.set($event)" />
        }
      </mat-tab>

      <mat-tab [label]="'waters.form.tab.owner-communication' | translate" disabled />
      <mat-tab [label]="'waters.form.tab.website' | translate" disabled />
    </mat-tab-group>
  </div>
}
